"use client";

import { CustomIcon } from "@/components/custom/icons/custom-icon";
import { Typography } from "@/components/custom/typography";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  useMediaCategories,
  useMediaCategoriesByStatus,
} from "@/hooks/useMediaCategories";
import { VehicleMedia } from "@/icons";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import { ListMediaCategory } from "./ListMediaCategory";
import { useRouter, useSearchParams } from "next/navigation";
import { useMediaCategoryStore } from "@/stores/mediaCategoryStore";
import { usePreloadSvgIcons } from "@/hooks/map-hooks/usePreloadSvgIcons";
import { useTypedTranslations } from "@/i18n/typed";

type Props = {
  openCategory?: boolean;
};

export default function CategoryFloatingButton({ openCategory }: Props) {
  // Multi-select state for category ids
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const router = useRouter();
  const searchParams = useSearchParams();
  const params = new URLSearchParams(searchParams.toString());
  const t = useTypedTranslations("mediaCategory");
  const setCategories = useMediaCategoryStore((s) => s.setCategories);

  const { data: mediaCategoriesData, isError: isErrorCategory } =
    useMediaCategories();
  usePreloadSvgIcons(
    Array.from(
      new Set(
        mediaCategoriesData?.data?.flatMap((c) => (c.icon ? [c.icon] : [])) ??
          []
      )
    )
  );
  const { data: publicCategoriesData, isError: isErrorCategoryStatus } =
    useMediaCategoriesByStatus();
  const [selectedVehicleType, setSelectedVehicleType] = useState<
    "VEHICLE_IN" | "VEHICLE_OUT" | "NON"
  >("NON");
  const [openVehicleDetail, setOpenVehicleDetail] = useState(false);
  const [openCategoryState, setOpenCategoryState] = useState(false);
  const publicCategories = isErrorCategory
    ? []
    : publicCategoriesData?.data || [];
  // check vehicle category existence
  const vehicleExist = new Map<string, boolean>();
  publicCategories.forEach((category) => {
    if (
      category.parent_cd === "VEHICLE_IN" ||
      category.parent_cd === "VEHICLE_OUT"
    ) {
      vehicleExist.set(category.parent_cd, true);
    }
  });
  const mediaCategories =
    isErrorCategoryStatus ||
    isErrorCategory ||
    mediaCategoriesData?.data?.length === 0 ||
    publicCategories?.length === 0
      ? {}
      : mediaCategoriesData?.data
          .filter((category) =>
            publicCategories.some(
              (publicCategory) => publicCategory.id === category.id
            )
          )
          .reduce((acc, category) => {
            const key = category.parent_cd;
            if (!acc[key]) {
              acc[key] = [];
            }
            acc[key].push(category);
            return acc;
          }, {} as { [key: string]: typeof mediaCategoriesData.data });

  const handleSelectedCategoryIds = (id: string) => {
    setSelectedCategoryIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  useEffect(() => {
    if (selectedCategoryIds.length > 0) {
      params.set("target_categories", selectedCategoryIds.join(","));
      router.replace(`?${params.toString()}`);
    } else {
      params.delete("target_categories");
      router.replace(`?${params.toString()}`);
    }
  }, [selectedCategoryIds]);

  useEffect(() => {
    if (mediaCategoriesData?.data) {
      setCategories(mediaCategoriesData?.data);
    }
  }, [mediaCategoriesData]);

  const VehicleTypeColumn: React.FC = () => {
    const items = [
      { key: "VEHICLE_IN" as const, label: t("Item.VEHICLE_IN") },
      { key: "VEHICLE_OUT" as const, label: t("Item.VEHICLE_OUT") },
    ];
    return (
      <div className="w-[166px] border-r border-gray-200">
        <span
          className={`font-pretendard font-light text-[8px] h-[14px] leading-[100%] tracking-[0] text-black mt-1 block text-center border-b border-gray-400 pb-1 w-full`}
        >
          {t("Item.MOBILITY")}
        </span>
        <ul>
          {items.length > 0 &&
            items.map(
              (item) =>
                vehicleExist.has(item.key) && (
                  <li
                    key={item.key}
                    onClick={() => {
                      setSelectedVehicleType(item.key);
                      if (
                        !openVehicleDetail ||
                        selectedVehicleType !== item.key
                      ) {
                        setOpenVehicleDetail(true);
                      }
                      if (selectedVehicleType == item.key) {
                        setSelectedVehicleType("NON");
                        setOpenVehicleDetail(false);
                      }
                    }}
                    className={`flex items-center justify-between px-[6px] py-[4px] cursor-pointer ${
                      selectedVehicleType === item.key
                        ? "bg-gray-50"
                        : "hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center gap-[4px]">
                      <VehicleMedia
                        className="w-[24px] h-[24px]"
                        style={{ color: "#616161" }}
                      />
                      <span className="font-pretendard font-normal text-[12px] leading-[120%] tracking-[-0.4px] text-gray-700">
                        {item.label}
                      </span>
                    </div>

                    <CustomIcon
                      name="ChervonRight"
                      className={cn("w-[10px] h-[10px] text-gray-700")}
                    />
                  </li>
                )
            )}
        </ul>
      </div>
    );
  };

  return (
    <div
      data-state={openCategory ? "open" : "closed"}
      // className={
      //   "fixed top-[calc(var(--header-height)+10px)] left-[4px] z-[1] transition-transform ease-in-out " +
      //   "data-[state=closed]:duration-300 data-[state=open]:duration-500 " +
      //   "data-[state=open]:translate-x-[402px] data-[state=closed]:translate-x-0"
      // }
      className="relative"
    >
      <Popover>
        <PopoverTrigger asChild>
          <Button
            type="button"
            className={`flex flex-col items-center justify-center h-[42px] w-[42px] mb-[1px] gap-[1px] rounded-[2px px-[2px] py-[4px] text-[8px] font-normal text-gray-700 shadow-md  transition-colors
               ${
                 openCategoryState
                   ? "bg-[#3E7EFF] hover:bg-[#3E7EFF]"
                   : "bg-white hover:bg-gray-100"
               }
              `}
            variant="secondary"
            classPrefix="mr-0"
            onClick={() => {
              setOpenCategoryState(!openCategoryState);
            }}
            prefix={
              <CustomIcon
                name="CategorySelect"
                className={cn(
                  openCategoryState
                    ? "text-white"
                    : selectedCategoryIds.length > 0
                    ? "text-primary"
                    : ""
                )}
              />
            }
          >
              <Typography
              className={`text-[8px] leading-[100%] tracking-[0] text-center w-full 
              ${
                openCategoryState
                  ? "text-white"
                  : selectedCategoryIds.length > 0 && "text-primary"
              }
              `}
            >
              {t("title")}
            </Typography>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="bg-white border w-auto border-gray-200 shadow-lg p-0 b-0"
          align="start"
        >
          <div className="relative flex">
            {mediaCategories && (
              <>
                <ListMediaCategory
                  parent={t("Item.IN")}
                  key="IN"
                  categories={
                    mediaCategories["IN"] != null
                      ? mediaCategories["IN"].map((c) => ({
                          id: c.id,
                          name: c.media_category_name,
                          icon: c.icon ?? "",
                        }))
                      : []
                  }
                  selectedCategoryIds={selectedCategoryIds}
                  onSelectCategory={(id) => handleSelectedCategoryIds(id)}
                />
                <ListMediaCategory
                  parent={t("Item.OUT")}
                  key="OUT"
                  categories={
                    mediaCategories["OUT"] != null
                      ? mediaCategories["OUT"].map((c) => ({
                          id: c.id,
                          name: c.media_category_name,
                          icon: c.icon ?? "",
                        }))
                      : []
                  }
                  selectedCategoryIds={selectedCategoryIds}
                  onSelectCategory={(id) => handleSelectedCategoryIds(id)}
                />
                {vehicleExist.size > 0 && <VehicleTypeColumn />}
                {openVehicleDetail && (
                  <ListMediaCategory
                    categories={
                      mediaCategories[`${selectedVehicleType}`] != null
                        ? mediaCategories[`${selectedVehicleType}`].map(
                            (c) => ({
                              id: c.id,
                              name: c.media_category_name,
                              icon: c.icon ?? "",
                            })
                          )
                        : []
                    }
                    selectedCategoryIds={selectedCategoryIds}
                    onSelectCategory={(id) => handleSelectedCategoryIds(id)}
                  />
                )}
              </>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
